import { useMemo, useState } from "react";
import { useServerFn } from "@/lib/use-server-fn";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { adminList, adminUpsert, adminDelete, type Row } from "@/lib/admin.functions";
import { adminListQuery } from "@/lib/admin-queries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { FileUpload } from "@/components/admin/FileUpload";

export type FieldType =
  | "text"
  | "textarea"
  | "richtext"
  | "number"
  | "boolean"
  | "select"
  | "date"
  | "time"
  | "image"
  | "file"
  | "slug";

export interface FieldSchema {
  name: string;
  label: string;
  type: FieldType;
  options?: { label: string; value: string }[];
  required?: boolean;
  placeholder?: string;
  fullWidth?: boolean;
}

export interface ColumnSchema {
  key: string;
  label: string;
  render?: (row: Row) => React.ReactNode;
}

function slugify(text: string) {
  return text
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");
}

function statusVariant(status: string) {
  if (status === "published") return "default" as const;
  if (status === "draft") return "secondary" as const;
  return "outline" as const;
}

export function CrudManager({
  table,
  label,
  fields,
  columns,
  slugSource = "title",
  idColumn = "id",
  imageFolder,
  extraFilter,
}: {
  table: string;
  label: string;
  fields: FieldSchema[];
  columns: ColumnSchema[];
  slugSource?: string;
  idColumn?: string;
  imageFolder?: string;
  extraFilter?: (row: Row) => boolean;
}) {
  const callList = useServerFn(adminList);
  const callUpsert = useServerFn(adminUpsert);
  const callDelete = useServerFn(adminDelete);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    ...adminListQuery(table),
    queryFn: () => callList({ data: { table: table as never } }),
  });

  const rows = useMemo(() => {
    const list = data ?? [];
    return extraFilter ? list.filter(extraFilter) : list;
  }, [data, extraFilter]);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Row | null>(null);
  const [form, setForm] = useState<Record<string, any>>({});
  const [slugTouched, setSlugTouched] = useState(false);

  const upsertMutation = useMutation({
    mutationFn: (values: Record<string, any>) => callUpsert({ data: { table: table as never, values } }),
    onSuccess: () => {
      toast.success("Salvo com sucesso");
      qc.invalidateQueries({ queryKey: ["admin", "list", table] });
      setOpen(false);
    },
    onError: (err: any) => toast.error(err?.message ?? "Erro ao salvar"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => callDelete({ data: { table: table as never, id } }),
    onSuccess: () => {
      toast.success("Excluído com sucesso");
      qc.invalidateQueries({ queryKey: ["admin", "list", table] });
    },
    onError: (err: any) => toast.error(err?.message ?? "Erro ao excluir"),
  });

  const openNew = () => {
    setEditing(null);
    const init: Record<string, any> = {};
    for (const f of fields) {
      if (f.type === "boolean") init[f.name] = false;
      else init[f.name] = "";
    }
    if (fields.some((f) => f.name === "status")) init["status"] = "draft";
    setForm(init);
    setSlugTouched(false);
    setOpen(true);
  };

  const openEdit = (row: Row) => {
    setEditing(row);
    setForm({ ...row });
    setSlugTouched(true);
    setOpen(true);
  };

  const handleChange = (name: string, value: any) => {
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === slugSource && !slugTouched && fields.some((f) => f.type === "slug")) {
        const slugField = fields.find((f) => f.type === "slug");
        if (slugField) next[slugField.name] = slugify(value ?? "");
      }
      return next;
    });
  };

  const handleSlugFocus = () => setSlugTouched(true);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    for (const f of fields) {
      if (f.required && !form[f.name]) {
        toast.error(`Preencha o campo "${f.label}"`);
        return;
      }
    }
    const values: Record<string, any> = { ...form };
    if (editing) values[idColumn] = editing[idColumn];
    for (const f of fields) {
      if (f.type === "number" && values[f.name] !== "" && values[f.name] != null) {
        values[f.name] = Number(values[f.name]);
      }
      if (values[f.name] === "") values[f.name] = null;
    }
    upsertMutation.mutate(values);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl text-foreground">{label}</h2>
        <Button onClick={openNew}>
          <Plus className="mr-2 size-4" /> Novo
        </Button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((c) => (
                <TableHead key={c.key}>{c.label}</TableHead>
              ))}
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length + 1} className="py-10 text-center text-muted-foreground">
                  <Loader2 className="mx-auto size-5 animate-spin" />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + 1} className="py-10 text-center text-muted-foreground">
                  Nenhum registro encontrado.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row[idColumn]}>
                  {columns.map((c) => (
                    <TableCell key={c.key} className="max-w-[280px] truncate">
                      {c.render
                        ? c.render(row)
                        : c.key === "status"
                          ? <Badge variant={statusVariant(row['status'])}>{row['status']}</Badge>
                          : String(row[c.key] ?? "")}
                    </TableCell>
                  ))}
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(row)}>
                        <Pencil className="size-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <Trash2 className="size-4 text-destructive" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Excluir registro?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Esta ação não pode ser desfeita.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteMutation.mutate(row[idColumn])}>
                              Excluir
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Editar ${label}` : `Novo em ${label}`}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {fields.map((f) => (
                <div key={f.name} className={f.fullWidth || f.type === "richtext" || f.type === "textarea" ? "sm:col-span-2 space-y-1.5" : "space-y-1.5"}>
                  <Label>
                    {f.label} {f.required && <span className="text-destructive">*</span>}
                  </Label>
                  {f.type === "text" && (
                    <Input
                      value={form[f.name] ?? ""}
                      placeholder={f.placeholder}
                      onChange={(e) => handleChange(f.name, e.target.value)}
                    />
                  )}
                  {f.type === "slug" && (
                    <Input
                      value={form[f.name] ?? ""}
                      onFocus={handleSlugFocus}
                      onChange={(e) => handleChange(f.name, slugify(e.target.value))}
                    />
                  )}
                  {f.type === "number" && (
                    <Input
                      type="number"
                      value={form[f.name] ?? ""}
                      onChange={(e) => handleChange(f.name, e.target.value)}
                    />
                  )}
                  {f.type === "date" && (
                    <Input
                      type="date"
                      value={form[f.name] ?? ""}
                      onChange={(e) => handleChange(f.name, e.target.value)}
                    />
                  )}
                  {f.type === "time" && (
                    <Input
                      type="time"
                      value={form[f.name] ?? ""}
                      onChange={(e) => handleChange(f.name, e.target.value)}
                    />
                  )}
                  {f.type === "textarea" && (
                    <Textarea
                      rows={4}
                      value={form[f.name] ?? ""}
                      placeholder={f.placeholder}
                      onChange={(e) => handleChange(f.name, e.target.value)}
                    />
                  )}
                  {f.type === "richtext" && (
                    <RichTextEditor
                      value={form[f.name] ?? ""}
                      onChange={(html) => handleChange(f.name, html)}
                    />
                  )}
                  {f.type === "boolean" && (
                    <div className="flex h-9 items-center">
                      <Switch
                        checked={!!form[f.name]}
                        onCheckedChange={(v) => handleChange(f.name, v)}
                      />
                    </div>
                  )}
                  {f.type === "select" && (
                    <Select value={form[f.name] ?? undefined} onValueChange={(v) => handleChange(f.name, v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        {f.options?.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {f.type === "image" && (
                    <ImageUpload
                      value={form[f.name]}
                      folder={imageFolder ?? table}
                      onChange={(url) => handleChange(f.name, url)}
                    />
                  )}
                  {f.type === "file" && (
                    <FileUpload
                      value={form[f.name]}
                      folder={imageFolder ?? table}
                      onChange={(url) => handleChange(f.name, url)}
                    />
                  )}
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={upsertMutation.isPending}>
                {upsertMutation.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
                Salvar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
